import { inject, Injectable, signal } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { ITelemetryData } from './ITelemetryData';
import { webSocket, WebSocketSubject } from 'rxjs/webSocket';
import { v4 as uuidv4 } from 'uuid';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { VisibilitiesService } from '../shared/visibilities.service';

enum ConnectionState {
	IDLE = 'idle',
	CONNECTED = 'connected',
	ERROR = 'error',
	DISCONNECTED = 'disconnected',
}

enum MessageType {
	CONNECTION_INIT = 'connection_init',
	CONNECTION_ACK = 'connection_ack',
	KA = 'ka',
	DATA = 'data',
	SUBSCRIBE = 'subscribe',
	SUBSCRKBE_SUCCESS = 'subscribe_success',
}

@Injectable({
	providedIn: 'root',
})
export class TelemetryService {
	private readonly WS_ENDPOINT =
		'wss://wzidxebhlbgqpm7kt22wkx2pri.appsync-realtime-api.us-east-1.amazonaws.com/event/realtime';
	private readonly PROTOCOLS = [
		'aws-appsync-event-ws',
		'header-eyJob3N0Ijoid3ppZHhlYmhsYmdxcG03a3QyMndreDJwcmkuYXBwc3luYy1hcGkudXMtZWFzdC0xLmFtYXpvbmF3cy5jb20iLCJ4LWFtei1kYXRlIjoiMjAyNjA0MDhUMjAxMzI0WiIsIngtYXBpLWtleSI6ImRhMi1weTQzNnJvZ2tuYndiZGt6amIyZGhzcHN6cSJ9',
	];

	readonly telemetryDataSubject = new BehaviorSubject<ITelemetryData[]>([]);
	readonly sessionSubject = new BehaviorSubject<any>([]);
	readonly connectionState = signal(ConnectionState.IDLE);

	private readonly visibilitiesService = inject(VisibilitiesService);

	private messageChannelId: string;
	private sessionChannelId: string;

	private readonly socket$: WebSocketSubject<any>;
	public telemetryData$ = this.telemetryDataSubject.asObservable();
	public session$ = this.sessionSubject.asObservable();

	constructor() {
		if (!this.visibilitiesService.controlVisibilities().showTelemetry) {
			return;
		}
		this.socket$ = webSocket({
			url: this.WS_ENDPOINT,
			protocol: this.PROTOCOLS,
		});

		this.socket$.pipe(takeUntilDestroyed()).subscribe({
			next: (message) => this.handleMessage(message),
			error: (error) => {
				console.error('Telemetry WebSocket error: ', error);
				this.connectionState.set(ConnectionState.ERROR);
			},
			complete: () => console.log('Telemetry WebSocket connection closed'),
		});

		this.sendInit();
	}

	private handleMessage(message: any) {
		const type: MessageType = message.type;
		switch (type) {
			case MessageType.CONNECTION_ACK: {
				this.handleConnectionAck();
				break;
			}
			case MessageType.KA: {
				this.handleKa();
				break;
			}
			case MessageType.DATA: {
				this.handleData(message);
				break;
			}
			case MessageType.SUBSCRKBE_SUCCESS: {
				this.handleSubscribeSuccess();
				break;
			}
			case MessageType.CONNECTION_INIT:
			case MessageType.SUBSCRIBE: {
				console.error('unexpected incoming message');
				break;
			}
		}
	}

	private sendInit() {
		console.log('[telem] initializing connection - state:', this.connectionState());
		this.socket$.next({
			type: MessageType.CONNECTION_INIT,
		});
	}

	private sendMessageSub() {
		console.log('[telem] subscribing to telemetry/message - state:', this.connectionState());
		const amzDate = new Date().toISOString().replace(/[:\-]/g, '').split('.')[0] + 'Z';
		this.messageChannelId = uuidv4();
		this.socket$.next({
			id: this.messageChannelId,
			channel: 'telemetry/message',
			authorization: {
				host: 'wzidxebhlbgqpm7kt22wkx2pri.appsync-api.us-east-1.amazonaws.com',
				'x-amz-date': amzDate,
				'x-api-key': 'da2-py436rogknbwbdkzjb2dhspszq',
				'x-amz-user-agent': 'aws-amplify/6.14.3 framework/0',
			},
			payload: {
				channel: 'telemetry/message',
				extensions: {
					authorization: {
						host: 'wzidxebhlbgqpm7kt22wkx2pri.appsync-api.us-east-1.amazonaws.com',
						'x-amz-date': amzDate,
						'x-api-key': 'da2-py436rogknbwbdkzjb2dhspszq',
						'x-amz-user-agent': 'aws-amplify/6.14.3 framework/0',
					},
				},
			},
			type: MessageType.SUBSCRIBE,
		});
	}

	private sendSessionSub() {
		console.log('[telem] subscribing to telemetry/session - state:', this.connectionState());
		const amzDate = new Date().toISOString().replace(/[:\-]/g, '').split('.')[0] + 'Z';
		this.sessionChannelId = uuidv4();
		this.socket$.next({
			id: this.sessionChannelId,
			channel: 'telemetry/session',
			authorization: {
				host: 'wzidxebhlbgqpm7kt22wkx2pri.appsync-api.us-east-1.amazonaws.com',
				'x-amz-date': amzDate,
				'x-api-key': 'da2-py436rogknbwbdkzjb2dhspszq',
				'x-amz-user-agent': 'aws-amplify/6.14.3 framework/0',
			},
			payload: {
				channel: 'telemetry/session',
				extensions: {
					authorization: {
						host: 'wzidxebhlbgqpm7kt22wkx2pri.appsync-api.us-east-1.amazonaws.com',
						'x-amz-date': amzDate,
						'x-api-key': 'da2-py436rogknbwbdkzjb2dhspszq',
						'x-amz-user-agent': 'aws-amplify/6.14.3 framework/0',
					},
				},
			},
			type: MessageType.SUBSCRIBE,
		});
	}

	private handleConnectionAck() {
		this.connectionState.set(ConnectionState.CONNECTED);
		console.log('[telem] connection acknowledged - state:', this.connectionState());
		this.sendMessageSub();
	}

	private handleData(message: any) {
		const base64Data = JSON.parse(message.event).data;
		const data: ITelemetryData[] = JSON.parse(atob(base64Data));
		if (message.id === this.messageChannelId) {
			this.telemetryDataSubject.next(data);
		} else if (message.id === this.sessionChannelId) {
			this.sessionSubject.next(data);
		} else {
			console.warn('Unhandled message channel or type: ', message);
		}
	}

	private handleSubscribeSuccess() {
		console.log('[telem] subscription success - state:', this.connectionState());
	}

	private handleKa() {
		console.log('[telem] keep alive - state:', this.connectionState());
	}
}
